/**
 * FieldEditor — renders a single field with schema-driven type, masking, and formatting.
 *
 * Supports: text, number/currency, date, enum (dropdown), masked (with reveal toggle).
 * Double-click to edit inline.
 */

import React, { useState } from 'react';
import { formatCurrency, formatDate } from '../../utils/formatters';
import { getFieldFormat, isFieldMasked, isFieldReadOnly, getFieldType, getFieldEnum, getEnumLabel, isPageLink, maskValue } from '../../utils/schemaUtils';
import styles from './FieldEditor.module.css';

function FieldEditor({ value, fieldSchema, fieldKey, onChange, onPageClick, readOnly: readOnlyView = false }) {
  const [editing, setEditing] = useState(false);
  const [localVal, setLocalVal] = useState(value);
  const [revealed, setRevealed] = useState(false);

  const format = getFieldFormat(fieldSchema);
  const masked = isFieldMasked(fieldSchema);
  const readOnly = readOnlyView || isFieldReadOnly(fieldSchema);
  const type = getFieldType(fieldSchema);
  const enumValues = getFieldEnum(fieldSchema);
  const pageLink = isPageLink(fieldSchema);

  // Format display value
  const formatDisplay = (val) => {
    if (val === null || val === undefined || val === '') return '—';
    if (masked && !revealed) return maskValue(val);
    if (format === 'currency') return formatCurrency(val);
    if (format === 'date') {
      try { return formatDate(val); } catch { return String(val); }
    }
    if (enumValues) return getEnumLabel(fieldSchema, val);
    return String(val);
  };

  // Determine input type
  const getInputType = () => {
    if (enumValues) return 'select';
    if (type === 'number' || type === 'integer' || format === 'currency') return 'number';
    if (format === 'date') return 'date';
    if (type === 'boolean') return 'checkbox';
    return 'text';
  };

  const handleCommit = (newVal) => {
    setEditing(false);
    let finalVal = newVal;
    const isNullable = Array.isArray(fieldSchema?.type) && fieldSchema.type.includes('null');

    if (type === 'number' || type === 'integer' || format === 'currency') {
      const trimmed = String(newVal ?? '').trim();
      if (trimmed === '') {
        finalVal = isNullable ? null : 0;
      } else {
        const parsed = parseFloat(trimmed);
        if (Number.isNaN(parsed)) {
          finalVal = isNullable ? null : 0;
        } else {
          finalVal = parsed;
        }
      }
    }
    if (format === 'date') {
      const trimmed = (newVal || '').trim();
      if (!trimmed) {
        finalVal = '';
      } else {
        const isoMatch = trimmed.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
        const usMatch = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
        let parsed = null;
        if (isoMatch) {
          parsed = new Date(parseInt(isoMatch[1]), parseInt(isoMatch[2]) - 1, parseInt(isoMatch[3]));
        } else if (usMatch) {
          parsed = new Date(parseInt(usMatch[3]), parseInt(usMatch[1]) - 1, parseInt(usMatch[2]));
        }
        if (!parsed || isNaN(parsed.getTime())) {
          alert('Invalid date. Use YYYY-MM-DD or M/D/YYYY format.');
          return;
        }
        const yyyy = parsed.getFullYear();
        const mm = String(parsed.getMonth() + 1).padStart(2, '0');
        const dd = String(parsed.getDate()).padStart(2, '0');
        finalVal = `${yyyy}-${mm}-${dd}`;
      }
    }
    if (finalVal !== value) {
      onChange(finalVal);
    }
  };

  // Editing mode
  if (editing) {
    const inputType = getInputType();

    if (inputType === 'select') {
      return (
        <select
          autoFocus
          className={styles.editInput}
          value={localVal || ''}
          onChange={(e) => { setLocalVal(e.target.value); handleCommit(e.target.value); }}
          onBlur={() => setEditing(false)}
        >
          <option value="">—</option>
          {enumValues.map(v => <option key={v} value={v}>{getEnumLabel(fieldSchema, v)}</option>)}
        </select>
      );
    }

    if (inputType === 'checkbox') {
      return (
        <input
          type="checkbox"
          checked={!!localVal}
          onChange={(e) => { onChange(e.target.checked); setEditing(false); }}
        />
      );
    }

    return (
      <input
        autoFocus
        className={styles.editInput}
        type={inputType === 'number' ? 'text' : inputType}
        value={localVal ?? ''}
        onChange={(e) => setLocalVal(e.target.value)}
        onBlur={(e) => handleCommit(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') handleCommit(e.target.value);
          if (e.key === 'Escape') { setLocalVal(value); setEditing(false); }
        }}
      />
    );
  }

  // Display mode
  const displayValue = formatDisplay(value);
  const clickable = pageLink && value;
  const warnNegative = fieldSchema?.['x-warn-if-negative'] === true && typeof value === 'number' && value < 0;

  return (
    <span className={styles.fieldValue}>
      <span
        className={`${styles.displayValue} ${clickable ? styles.pageLink : ''} ${warnNegative ? styles.warnNegative : ''}`}
        onDoubleClick={readOnly ? undefined : () => { setLocalVal(value); setEditing(true); }}
        onClick={clickable ? () => onPageClick?.(value) : undefined}
        title={warnNegative ? 'Negative value — verify this is intentional (overdraft or overpayment)' : (readOnly ? value : (clickable ? `Go to page ${value}` : 'Double-click to edit'))}
      >
        {warnNegative && '⚠ '}{displayValue}
      </span>
      {masked && (
        <button
          className={styles.revealBtn}
          onClick={() => setRevealed(!revealed)}
          title={revealed ? 'Hide' : 'Reveal'}
        >
          {revealed ? '🔒' : '👁'}
        </button>
      )}
    </span>
  );
}

export default FieldEditor;
